// Composer-adjacent queries shared between the feed route, the post composer,
// and the share-entity modal. Single source for "what groups can the viewer
// post into" + the mapped `ComposerGroup[]` shape the composer expects.
//
// One React Query entry keyed by ['feed', 'me', 'groups'] backs both the
// raw view (used by the feed route for membership counts + onboarding step
// thresholds) and the mapped `ComposerGroup[]` view (used by every composer
// host). Same fetch, two derived views, zero duplication.

import { queryOptions, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { getApiClient } from '@/lib/api-client';
import type { ComposerGroup } from '@/components/feed/PostComposer';

export type MyGroupsView = {
  circles: ReadonlyArray<{ id: string; name: string; role: 'leader' | 'member' }>;
  networks: ReadonlyArray<{ id: string; name: string }>;
};

async function fetchMyGroups(): Promise<MyGroupsView> {
  const client = getApiClient();
  const [circlesRes, networksRes] = await Promise.all([
    client.GET('/api/v1/members/me/circles', {
      params: { query: { pageSize: 100 } },
    }),
    client.GET('/api/v1/members/me/networks', {
      params: { query: { pageSize: 100 } },
    }),
  ]);
  const circles =
    circlesRes.data?.items.map((c) => ({
      id: c.id,
      name: c.name,
      role: c.role === 'leader' ? ('leader' as const) : ('member' as const),
    })) ?? [];
  const networks =
    networksRes.data?.items.map((n) => ({
      id: n.id,
      name: n.name,
    })) ?? [];
  return { circles, networks };
}

export const composerKeys = {
  myGroups: () =>
    queryOptions({
      queryKey: ['feed', 'me', 'groups'] as const,
      queryFn: fetchMyGroups,
    }),
};

export function useMyGroups(opts: { enabled?: boolean } = {}) {
  return useQuery({
    ...composerKeys.myGroups(),
    enabled: opts.enabled ?? true,
  });
}

/** Mapped view ready for `PostComposerSheet` / `ShareEntityModal`. Reuses the
 *  same React Query entry as `useMyGroups()` so simultaneous callers share one
 *  network request. Returns `null` until the underlying query resolves. */
export function useComposerGroups(opts: { enabled?: boolean } = {}): {
  data: ReadonlyArray<ComposerGroup> | null;
  isLoading: boolean;
} {
  const query = useMyGroups(opts);
  const data = useMemo(() => {
    const g = query.data;
    if (g === undefined) return null;
    return [
      ...g.circles.map((c) => ({
        id: c.id,
        name: c.name,
        kind: 'circle' as const,
      })),
      ...g.networks.map((n) => ({
        id: n.id,
        name: n.name,
        kind: 'network' as const,
      })),
    ];
  }, [query.data]);
  return { data, isLoading: query.isLoading };
}
